// Code Block with Syntax Highlighting
import hljs from 'highlight.js';
import 'highlight.js/styles/github-dark.css';

class CodeBlockWithHighlight {
  static get toolbox() {
    return {
      title: 'Code',
      icon: '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="16 18 22 12 16 6"></polyline><polyline points="8 6 2 12 8 18"></polyline></svg>'
    };
  }

  static get isReadOnlySupported() {
    return true;
  }

  constructor({ data, config, api, readOnly }) {
    this.data = {
      code: data.code || '',
      language: data.language || 'javascript',
      filename: data.filename || ''
    };
    this.config = config;
    this.api = api;
    this.readOnly = readOnly;
    this.wrapper = null;
    this.languages = [
      'javascript', 'typescript', 'python', 'java', 'cpp', 'csharp',
      'go', 'rust', 'ruby', 'php', 'swift', 'kotlin', 'scala',
      'html', 'css', 'scss', 'json', 'xml', 'yaml', 'markdown',
      'sql', 'bash', 'powershell', 'dockerfile', 'nginx',
      'graphql', 'plaintext'
    ];
  }

  render() {
    this.wrapper = document.createElement('div');
    this.wrapper.classList.add('code-block-highlight');
    this._renderCodeBlock();
    return this.wrapper;
  }

  _renderCodeBlock() {
    const highlighted = this.data.code 
      ? hljs.highlight(this.data.code, { language: this.data.language, ignoreIllegals: true }).value
      : '';

    this.wrapper.innerHTML = `
      <div class="rounded-lg overflow-hidden border border-slate-200 dark:border-slate-700 bg-slate-900" data-testid="code-block">
        <div class="flex items-center justify-between px-4 py-2 bg-slate-800 border-b border-slate-700">
          <div class="flex items-center gap-3">
            <div class="flex gap-1.5">
              <div class="w-3 h-3 rounded-full bg-red-500"></div>
              <div class="w-3 h-3 rounded-full bg-yellow-500"></div>
              <div class="w-3 h-3 rounded-full bg-green-500"></div>
            </div>
            ${!this.readOnly ? `
              <input 
                type="text" 
                class="filename-input text-xs px-2 py-1 bg-slate-700 border border-slate-600 rounded text-slate-300 placeholder-slate-500 w-32"
                placeholder="filename.js"
                value="${this.data.filename}"
              />
              <select class="language-select text-xs px-2 py-1 bg-slate-700 border border-slate-600 rounded text-slate-300">
                ${this.languages.map(lang => `
                  <option value="${lang}" ${this.data.language === lang ? 'selected' : ''}>${lang}</option>
                `).join('')}
              </select>
            ` : `
              ${this.data.filename ? `<span class="text-xs text-slate-400">${this.data.filename}</span>` : ''}
              <span class="text-xs text-slate-500">${this.data.language}</span>
            `}
          </div>
          <button class="copy-btn flex items-center gap-1 px-2 py-1 text-xs text-slate-400 hover:text-white transition-colors rounded hover:bg-slate-700" data-testid="copy-code-button">
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path></svg>
            <span class="copy-text">Copy</span>
          </button>
        </div>
        <div class="relative">
          ${!this.readOnly ? `
            <textarea 
              class="code-input w-full p-4 bg-slate-900 text-slate-100 font-mono text-sm resize-none focus:outline-none min-h-[150px]"
              placeholder="Enter your code here..."
              spellcheck="false"
            >${this.data.code}</textarea>
          ` : `
            <pre class="p-4 overflow-x-auto"><code class="hljs language-${this.data.language} text-sm">${highlighted || '<span class="text-slate-500">// No code</span>'}</code></pre>
          `}
        </div>
      </div>
    `;

    // Event listeners
    if (!this.readOnly) {
      const codeInput = this.wrapper.querySelector('.code-input');
      const languageSelect = this.wrapper.querySelector('.language-select');
      const filenameInput = this.wrapper.querySelector('.filename-input');

      if (codeInput) {
        codeInput.addEventListener('input', (e) => {
          this.data.code = e.target.value;
        });
        // Auto-resize
        codeInput.addEventListener('input', () => {
          codeInput.style.height = 'auto';
          codeInput.style.height = codeInput.scrollHeight + 'px';
        });
      }

      if (languageSelect) {
        languageSelect.addEventListener('change', (e) => {
          this.data.language = e.target.value;
        });
      }

      if (filenameInput) {
        filenameInput.addEventListener('input', (e) => {
          this.data.filename = e.target.value;
        });
      }
    }

    // Copy button
    const copyBtn = this.wrapper.querySelector('.copy-btn');
    if (copyBtn) {
      copyBtn.addEventListener('click', () => {
        navigator.clipboard.writeText(this.data.code).then(() => {
          const copyText = copyBtn.querySelector('.copy-text');
          copyText.textContent = 'Copied!';
          setTimeout(() => {
            copyText.textContent = 'Copy';
          }, 2000);
        });
      });
    }
  }

  save() {
    return {
      code: this.data.code,
      language: this.data.language,
      filename: this.data.filename
    };
  }

  static get sanitize() {
    return {
      code: true,
      language: false,
      filename: false
    };
  }
}

export default CodeBlockWithHighlight;
